/**
 * Module dependencies.
 */

import { Category } from 'src/types/news-exhibitions';
import { ReactElement, useCallback, useMemo } from 'react';
import { Type, color } from '@untile/react-components';
import { ifProp, theme } from 'styled-tools';
import { newsExhibitionsCategories } from 'src/core/content-config/news-exhibitions';
import { useRouter } from 'next/router';
import qs from 'qs';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  name: string;
  type: Category | 'all';
};

/**
 * `FilterListItem` styled component.
 */

const FilterListItem = styled.li`
  position: relative;
`;

/**
 * `FilterItemLink` styled component.
 */

const FilterItemLink = styled.button`
  appearance: none;
  background-color: ${color.transparentize('white', 0)};
  border: 0;
  cursor: pointer;
  outline: none;
  padding: 0;
  position: relative;
`;

/**
 * `FilterItemLabel` styled component.
 */

const FilterItemLabel = styled(Type.Paragraph)<{ active: boolean }>`
  color: ${color('textColor')};

  &::after {
    background-color: ${color('textColor')};
    bottom: 0;
    content: '';
    height: 1px;
    left: 0;
    position: absolute;
    transform: scaleX(0);
    transform-origin: bottom right;
    transition: transform ${theme('animations.defaultTransition')};
    width: 100%;
    will-change: transform;
  }

  &:focus,
  &:hover {
    &::after {
      transform: scaleX(1);
      transform-origin: bottom left;
    }
  }

  ${ifProp(
    'active',
    css`
      &::after {
        transform: scaleX(1);
        transform-origin: bottom left;
      }
    `
  )}
`;

/**
 * `Filter` component.
 */

const Filter = (props: Props): ReactElement => {
  const { className, name, type } = props;
  const router = useRouter();
  const category = useMemo<Category | undefined>(() => {
    return newsExhibitionsCategories[type] ?? undefined;
  }, [type]);

  const active = useMemo<boolean>(() => {
    const categoryQuery = router?.query?.category;

    return (
      categoryQuery === newsExhibitionsCategories[type] ||
      (type === 'all' && !categoryQuery)
    );
  }, [router?.query?.category, type]);

  const handleClickCategory = useCallback(() => {
    router.push(
      {
        pathname: router.pathname,
        ...(category && { query: qs.stringify({ category }) })
      },
      undefined,
      { shallow: true }
    );
  }, [category, router]);

  return (
    <FilterListItem className={className}>
      <FilterItemLink onClick={handleClickCategory}>
        <FilterItemLabel active={active}>{name}</FilterItemLabel>
      </FilterItemLink>
    </FilterListItem>
  );
};

/**
 * Export `Filter` component.
 */

export default Filter;
